/** `/dev/kit` (dev builds only): every console primitive with sample props so feature agents can see them. */
import { ClaimStatus, LotteryStatus, PaymentStatus } from '@windrop/contracts';
import { Button, useToast } from '@windrop/ui';
import { useState } from 'react';
import { BarsChart, TrendChart } from '../../components/Chart';
import { ConfirmDestructive } from '../../components/ConfirmDestructive';
import { DataTable, type Column } from '../../components/DataTable';
import { EmptyState, NoDataYet } from '../../components/EmptyState';
import { JsonDiff } from '../../components/JsonDiff';
import { MaskedCell } from '../../components/MaskedCell';
import { PageHeader } from '../../components/PageHeader';
import { QueueList } from '../../components/QueueList';
import { StatCard } from '../../components/StatCard';
import { StatusBadge } from '../../components/StatusBadge';
import { Timeline } from '../../components/Timeline';

interface KitRow {
  id: string;
  name: string;
  status: LotteryStatus;
  entries: number;
}

const ROWS: KitRow[] = Array.from({ length: 60 }, (_, i) => ({
  id: `L${i + 1}`,
  name: `Sample lottery ${i + 1}`,
  status: LotteryStatus.options[i % LotteryStatus.options.length]!,
  entries: (i * 37) % 500,
}));

const COLUMNS: Column<KitRow>[] = [
  { id: 'name', header: 'Name', sortValue: (r) => r.name.toLowerCase(), cell: (r) => r.name },
  {
    id: 'status',
    header: 'Status',
    sortValue: (r) => r.status,
    cell: (r) => <StatusBadge kind="LotteryStatus" value={r.status} />,
  },
  {
    id: 'entries',
    header: 'Entries',
    sortValue: (r) => r.entries,
    align: 'end',
    cell: (r) => <span className="wd-tabular">{r.entries}</span>,
  },
];

export function DevKitPage() {
  const [confirm, setConfirm] = useState(false);
  const { toast } = useToast();
  return (
    <div className="wdc-kit">
      <PageHeader
        title="Console kit"
        description="Primitives in src/components. Dev builds only."
        breadcrumb={[{ label: 'Dev' }, { label: 'Kit' }]}
      />
      <section>
        <h2>StatusBadge</h2>
        <div className="wd-row" style={{ flexWrap: 'wrap' }}>
          {LotteryStatus.options.map((s) => (
            <StatusBadge key={s} kind="LotteryStatus" value={s} />
          ))}
          {PaymentStatus.options.map((s) => (
            <StatusBadge key={s} kind="PaymentStatus" value={s} size="sm" />
          ))}
          {ClaimStatus.options.map((s) => (
            <StatusBadge key={s} kind="ClaimStatus" value={s} size="sm" />
          ))}
        </div>
      </section>
      <section>
        <h2>StatCard</h2>
        <div className="wdc-grid">
          <StatCard
            label="Confirmed entries"
            value="12,480"
            hint="last 24 h"
            signal={{ tone: 'success', text: 'Queue green' }}
          />
          <StatCard label="Claims open" value={null} emptyHint="Appears with the first claim." />
          <StatCard label="Loading" value={null} loading />
        </div>
      </section>
      <section>
        <h2>Charts</h2>
        <div className="wdc-grid">
          <TrendChart
            title="Entries"
            data={[
              { h: '10:00', n: 12 },
              { h: '11:00', n: 30 },
              { h: '12:00', n: 22 },
            ]}
            xKey="h"
            series={[{ key: 'n', label: 'Entries' }]}
          />
          <BarsChart
            title="By platform"
            data={[
              { p: 'WHATSAPP', n: 40 },
              { p: 'INSTAGRAM', n: 25 },
            ]}
            xKey="p"
            series={[{ key: 'n', label: 'Entries', color: 'secondary' }]}
          />
          <TrendChart title="Empty" data={[]} xKey="h" series={[{ key: 'n', label: 'Entries' }]} />
        </div>
      </section>
      <section>
        <h2>DataTable (virtualized, selectable, dry-run bulk action)</h2>
        <DataTable
          caption="Kit rows"
          columns={COLUMNS}
          rows={ROWS}
          rowKey={(r) => r.id}
          selectable
          pageSize={0}
          virtualize
          maxHeight={320}
          bulkActions={[
            {
              id: 'pause',
              label: 'Pause',
              dryRun: (rows) => ({
                affected: rows.filter((r) => r.status === 'LIVE').length,
                detail: 'only LIVE rows pause',
              }),
              run: (rows) =>
                toast({ title: `Would pause ${rows.filter((r) => r.status === 'LIVE').length} lotteries` }),
            },
          ]}
          rowActions={(r) => (
            <Button variant="quiet" onClick={() => toast({ title: r.name })}>
              Open
            </Button>
          )}
        />
      </section>
      <section>
        <h2>MaskedCell</h2>
        <MaskedCell
          masked="+234•••••••12"
          what="WhatsApp number"
          subject={{ kind: 'CLAIM_IDENTITY', claimId: '01ARZ3NDEKTSV4RRFFQ69G5FAV' }}
          unmask={async () => ({
            value: '+2348000000012',
            validUntil: new Date(Date.now() + 15_000).toISOString(),
            auditId: '01ARZ3NDEKTSV4RRFFQ69G5FAV',
          })}
        />
      </section>
      <section>
        <h2>ConfirmDestructive</h2>
        <Button variant="danger" onClick={() => setConfirm(true)}>
          Cancel lottery
        </Button>
        <ConfirmDestructive
          open={confirm}
          onOpenChange={setConfirm}
          title="Cancel lottery"
          objectName="aurora-launch-raffle"
          objectKind="lottery slug"
          consequence="Refunds every confirmed paid entry and sends the cancellation template."
          affected={{ affected: 1240, detail: '1,180 free, 60 paid' }}
          requireReason
          onConfirm={({ reason }) => {
            toast({ title: 'Confirmed', description: reason });
            setConfirm(false);
          }}
        />
      </section>
      <section>
        <h2>JsonDiff</h2>
        <JsonDiff
          before={{ entry: { mode: 'FREE', caps: { perPerson: 1 } }, prizes: ['a'] }}
          after={{ entry: { mode: 'PAID', caps: { perPerson: 3, perDay: 1 } }, prizes: ['a', 'b'] }}
          beforeLabel="v1"
          afterLabel="v2"
        />
      </section>
      <section>
        <h2>QueueList</h2>
        <QueueList
          items={[
            {
              id: '1',
              title: 'Claim WIN-ABCDE-FGHJK',
              subtitle: 'Verification pending',
              since: new Date(Date.now() - 3600_000).toISOString(),
              deadline: new Date(Date.now() + 86_400_000).toISOString(),
              owner: 'Ada',
              status: { tone: 'warning', text: 'Waiting on provider' },
              nextAction: { label: 'Review', to: '/claims' },
            },
            {
              id: '2',
              title: 'Payout P-204',
              since: new Date(Date.now() - 2 * 86_400_000).toISOString(),
              deadline: new Date(Date.now() - 3600_000).toISOString(),
              owner: null,
              status: { tone: 'info', text: 'Pending approval' },
              nextAction: { label: 'Approve', to: '/finance' },
            },
          ]}
        />
      </section>
      <section>
        <h2>Timeline</h2>
        <Timeline
          events={[
            {
              id: 'a',
              at: new Date().toISOString(),
              title: 'Published',
              actor: 'staff approver@windrop.test',
              tone: 'success',
            },
            {
              id: 'b',
              at: new Date().toISOString(),
              title: 'Paused',
              actor: 'staff admin@windrop.test',
              tone: 'warning',
              detail: 'Reason: sponsor asset failed contrast.',
            },
          ]}
        />
      </section>
      <section>
        <h2>EmptyState / NoDataYet</h2>
        <EmptyState
          title="No claims yet"
          body="Claims appear after the first win."
          action={<Button href="/">Dashboard</Button>}
        />
        <NoDataYet what="entries" hint="Appears once the lottery is live." />
      </section>
    </div>
  );
}
